import { useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  useCreateChangeRequest,
  useMyChangeRequests,
} from '@/hooks/react-query/useChangeRequests';
import { useToast } from '@/hooks/useToast';
import { AlertCircle, MapPin, CreditCard, Send, Clock } from 'lucide-react';
import { BankingSection } from './BankingSection';
import type { BankingSectionHandle } from './BankingSection';

interface RequestChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fieldType?: 'banking' | 'address' | 'general';
  currentValues?: Record<string, unknown>;
  // For unified dialog (from EntityEditModal)
  entity?: {
    id: string;
    name: string;
    address?: {
      line1: string;
      line2: string;
      city: string;
      postcode: string;
      country: string;
    };
    bankDetails?: {
      accountHolder: string;
      bankName: string;
      iban: string;
      swift?: string;
      accountNumber?: string;
      bankCountry?: string;
      bankAddress?: string;
    };
  };
}

const FIELD_CONFIG = {
  address: [
    { key: 'address_line_1', label: 'Street Address', type: 'text' as const },
    { key: 'address_line_2', label: 'Address Line 2', type: 'text' as const },
    { key: 'city', label: 'City', type: 'text' as const },
    { key: 'postcode', label: 'Postal Code', type: 'text' as const },
    { key: 'country', label: 'Country', type: 'text' as const },
  ],
  general: [
    {
      key: 'registration_number',
      label: 'Registration Number',
      type: 'text' as const,
    },
    { key: 'legal_form', label: 'Legal Form', type: 'text' as const },
    {
      key: 'taxpayer_reference_number',
      label: 'Tax ID',
      type: 'text' as const,
    },
  ],
};

// Normalize a value for no-op comparison, mirroring the backend: banking
// identifiers carry formatting that doesn't change their meaning (IBAN
// spacing/case, a BIC's optional XXX branch padding, bank-name casing), so a
// cosmetic re-save shouldn't be submitted as a change.
function normalizeChangeValue(key: string, value: unknown): string {
  const text = (value ?? '').toString().trim();
  if (key === 'iban') return text.replace(/\s+/g, '').toUpperCase();
  if (key === 'bank_swift_code') {
    return text
      ? text.replace(/\s+/g, '').toUpperCase().padEnd(11, 'X').slice(0, 11)
      : '';
  }
  if (key === 'bank_name') return text.toLowerCase();
  return text;
}

function PendingBadge() {
  return (
    <Badge variant="outline" className="ml-1 flex items-center gap-1">
      <Clock className="h-3 w-3" />
      Pending
    </Badge>
  );
}

export function RequestChangeDialog({
  isOpen,
  onClose,
  fieldType,
  currentValues,
  entity,
}: RequestChangeDialogProps) {
  const [activeTab, setActiveTab] = useState<string>('address');
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const bankingRef = useRef<BankingSectionHandle>(null);

  const createMutation = useCreateChangeRequest();
  const { data: myChangeRequests } = useMyChangeRequests();
  const { toast } = useToast();

  // Determine if this is a unified dialog (tabs) or single-type dialog
  const isUnifiedDialog = !fieldType && entity;

  const pendingRequestTypesForEntity = entity
    ? new Set(
        (myChangeRequests?.items ?? [])
          .filter(
            (request) =>
              request.investor_id === entity.id && request.status === 'pending'
          )
          .map((request) => request.request_type)
      )
    : new Set<string>();

  const hasPendingAddressRequest =
    pendingRequestTypesForEntity.has('address_update');
  const hasPendingBankingRequest =
    pendingRequestTypesForEntity.has('banking_update');
  const hasAnyPendingRequest =
    hasPendingAddressRequest || hasPendingBankingRequest;

  const resetForm = () => {
    setNewValues({});
    setReason('');
    setErrors({});
    setActiveTab('address');
  };

  // Get current values for a field type
  const getCurrentValues = (
    type: 'banking' | 'address'
  ): Record<string, unknown> => {
    if (currentValues) return currentValues;

    if (!entity) return {};

    if (type === 'address' && entity.address) {
      return {
        address_line_1: entity.address.line1,
        address_line_2: entity.address.line2,
        city: entity.address.city,
        postcode: entity.address.postcode,
        country: entity.address.country,
      };
    }

    if (type === 'banking' && entity.bankDetails) {
      return {
        bank_account_name: entity.bankDetails.accountHolder,
        iban: entity.bankDetails.iban,
        bank_account_number: entity.bankDetails.accountNumber || '',
        bank_swift_code: entity.bankDetails.swift || '',
        bank_name: entity.bankDetails.bankName,
        bank_country: entity.bankDetails.bankCountry || '',
        bank_address: entity.bankDetails.bankAddress || '',
      };
    }

    return {};
  };

  const handleSubmit = async () => {
    setErrors({});

    // Validate that at least one field has been modified
    if (Object.keys(newValues).length === 0) {
      setErrors({ general: 'Please provide at least one field to update' });
      return;
    }

    // Validate reason is provided
    if (!reason.trim()) {
      setErrors({ reason: 'Please provide a reason for this change' });
      return;
    }

    // Banking section enforces its own rules (e.g. a non-IBAN account needs a
    // bank country). It surfaces the error inline, so just block submission.
    if (bankingRef.current && !bankingRef.current.validate()) {
      return;
    }

    // Drop fields that match the current value (modulo formatting) so a cosmetic
    // re-save - e.g. the IBAN auto-fill rewriting bank name casing or padding the
    // BIC - doesn't reach the reviewer as an identical current/proposed diff.
    const current = {
      ...getCurrentValues('address'),
      ...getCurrentValues('banking'),
      ...(currentValues ?? {}),
    };
    const changedValues = Object.fromEntries(
      Object.entries(newValues).filter(
        ([key, value]) =>
          normalizeChangeValue(key, value) !==
          normalizeChangeValue(key, current[key])
      )
    );

    if (Object.keys(changedValues).length === 0) {
      setErrors({
        general: 'No changes detected - update a field before submitting.',
      });
      return;
    }

    try {
      await createMutation.mutateAsync({
        new_values: changedValues,
        change_reason: reason,
      });

      // Reset form and close
      setNewValues({});
      setReason('');
      setErrors({});
      onClose();

      // Show success message
      toast({
        title: 'Change Data Submitted',
        description:
          'Your change data has been submitted for admin approval. You will be notified once it has been reviewed.',
      });
    } catch (error) {
      console.error('Failed to submit change data:', error);
      setErrors({
        general: 'Failed to submit change data. Please try again.',
      });
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const renderFieldSection = (type: 'address' | 'general') => {
    const fields = FIELD_CONFIG[type];
    const current = getCurrentValues(type as 'address');

    return (
      <div className="space-y-4">
        {fields.map((field) => {
          const fieldValue =
            newValues[field.key] ??
            (current[field.key] ? String(current[field.key]) : '');

          return (
            <div key={field.key} className="space-y-2">
              <Label htmlFor={field.key}>{field.label}</Label>
              <Input
                id={field.key}
                type={field.type}
                placeholder={current[field.key] ? '' : 'Not set'}
                value={fieldValue}
                onChange={(e) =>
                  setNewValues({ ...newValues, [field.key]: e.target.value })
                }
              />
            </div>
          );
        })}
      </div>
    );
  };

  const legacyFieldSection = fieldType === 'address' ? 'address' : 'general';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Changes</DialogTitle>
          <DialogDescription>
            {isUnifiedDialog
              ? `Submit a change data for ${entity?.name}. Changes require administrative approval and will be reviewed before being applied.`
              : 'Submit a change data for approval. Changes will be reviewed by an administrator before being applied.'}
          </DialogDescription>
        </DialogHeader>

        {errors.general && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errors.general}</AlertDescription>
          </Alert>
        )}

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Important:</strong> All changes require administrator
            approval
          </AlertDescription>
        </Alert>

        {hasAnyPendingRequest && (
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertDescription>
              You have changes awaiting administrator approval. The fields below
              still show the currently approved values.{' '}
              <Link
                to="/portal/change-data"
                className="font-medium underline underline-offset-2"
              >
                View your pending changes
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}

        {isUnifiedDialog ? (
          // Unified dialog with tabs
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="address" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Address
                {hasPendingAddressRequest && <PendingBadge />}
              </TabsTrigger>
              <TabsTrigger value="banking" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Banking
                {hasPendingBankingRequest && <PendingBadge />}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="address" className="space-y-4 mt-4">
              <div className="flex items-center space-x-2 mb-4">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-medium">Address Information</h3>
              </div>
              {renderFieldSection('address')}
            </TabsContent>

            <TabsContent value="banking" className="space-y-4 mt-4">
              <div className="flex items-center space-x-2 mb-4">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-medium">Banking Information</h3>
              </div>
              <BankingSection
                ref={bankingRef}
                isOpen={isOpen}
                currentValues={getCurrentValues('banking')}
                newValues={newValues}
                setNewValues={setNewValues}
              />
            </TabsContent>
          </Tabs>
        ) : (
          // Single-type dialog (legacy mode)
          <div className="space-y-4">
            {fieldType === 'banking' ? (
              <BankingSection
                ref={bankingRef}
                isOpen={isOpen}
                currentValues={getCurrentValues('banking')}
                newValues={newValues}
                setNewValues={setNewValues}
              />
            ) : (
              renderFieldSection(legacyFieldSection)
            )}
          </div>
        )}

        {/* Reason for Change - always shown */}
        <div className="space-y-2 mt-6">
          <Label htmlFor="reason">
            Reason for Change <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please explain why you need to make these changes..."
            rows={4}
            className={errors.reason ? 'border-red-500' : ''}
          />
          {errors.reason && (
            <p className="text-sm text-red-600">{errors.reason}</p>
          )}
          <p className="text-sm text-muted-foreground">
            This information helps administrators review your request.
            <br />
            Due to compliance regulation the fund needs to approve information
            request.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              createMutation.isPending || Object.keys(newValues).length === 0
            }
          >
            <Send className="h-4 w-4 mr-2" />
            {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
